/**
 * Normalizes the configured public websites domain by removing the protocol
 * and trailing slashes.
 *
 * @param value - Raw environment value.
 * @returns The bare domain, or `undefined` when it is not configured.
 */
export const normalizeWebsiteBaseDomain = (
  value: string | undefined
): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }

  return trimmed.replace(/^https?:\/\//, '').replace(/\/+$/, '');
};

/** Public domain under which provisional website addresses live. */
export const WEBSITES_BASE_DOMAIN = normalizeWebsiteBaseDomain(
  import.meta.env.VITE_WEBSITES_BASE_DOMAIN
);

/**
 * Builds the hostname of a provisional website address.
 *
 * @param provisionalSlug - Website slug.
 * @param baseDomain - Public websites domain.
 * @returns The hostname, or `undefined` without a slug or domain.
 */
export const buildProvisionalSiteHost = (
  provisionalSlug: string,
  baseDomain: string | undefined = WEBSITES_BASE_DOMAIN
): string | undefined =>
  provisionalSlug && baseDomain
    ? `${provisionalSlug}.${baseDomain}`
    : undefined;

/**
 * Builds the URL of a provisional website address.
 *
 * @param provisionalSlug - Website slug.
 * @returns The HTTPS URL, or `undefined` when the public domain is not
 * configured.
 */
export const buildProvisionalSiteUrl = (
  provisionalSlug: string
): string | undefined => {
  const host = buildProvisionalSiteHost(provisionalSlug);

  return host ? `https://${host}` : undefined;
};

/**
 * Strips the protocol and trailing slashes from a site URL for display.
 *
 * @param url - Absolute site URL.
 * @returns The host and path, such as `corredora.websites.comos.ai`.
 */
export const getWebsiteUrlHost = (url: string): string =>
  url.replace(/^https?:\/\//, '').replace(/\/+$/, '');

/**
 * Resolves the hostname shown in the preview browser bar: the live address
 * when the site is published, the provisional one otherwise.
 *
 * @param liveSiteUrl - Public URL of the live site, when published.
 * @param provisionalSlug - Website slug.
 * @returns The hostname, or `undefined` when neither is known.
 */
export const resolveWebsitePreviewHost = (
  liveSiteUrl: string | null,
  provisionalSlug: string
): string | undefined =>
  liveSiteUrl
    ? getWebsiteUrlHost(liveSiteUrl)
    : buildProvisionalSiteHost(provisionalSlug);
